<template>
  <donut-page-sections-two-column :title="`${$i18n.t('shoutouts.feature')} channels`">
    <template #left-side>
      <div class="t dark-gray">
        <span>{{ channelsHelp }}</span><br><br>
        <span>You can use {{ $i18n.t('shoutouts.feature') }} in channels like: {{ displayChannelPhrase }}.</span>
      </div>
    </template>
    <template #right-side>
      <donut-selector-prefabs-channel
        v-model="channelsSelection"
        :is-multi="true"
        :include-private="true"
        :min-selections="1"
        :update-function="onChannelsUpdate"
        :loading="isLoading"
      />
      <div class="t link purple c-p m-t" @click="openNewChannelModal">Create a new Slack channel</div>
      <div class="f-ac f-je m-tx2">
        <donut-input-checkbox :model-value="restrictSelection" :label="restrictLabel" @update:model-value="onRestrictChange" />
        <i v-tooltip="restrictTooltip" class="mi md-16 m-lx1 dark-gray">info</i>
      </div>
    </template>
  </donut-page-sections-two-column>
</template>

<script setup>
  import { ref, computed, inject } from 'vue';
  import { useStore } from 'vuex';
  import useStringFormatting from 'composables/string-formatting';

  const $i18n = inject('$i18n');
  const { state, commit, dispatch } = useStore();
  const { toSentence } = useStringFormatting();
  const props = defineProps({
    channelPhrase: { type: String, required: true },
  });

  const program = computed(() => state.shoutoutsProgram.program);

  const featureName = $i18n.t('shoutouts.feature');
  const restrictLabel = `Only use ${featureName} in selected channels`;
  const restrictTooltip = `By default, you can use ${featureName} in all channels Donut is in. By checking this box, you limit ${featureName} just to your selected channels, useful for testing and team- or function-specific recognition programs.`;
  const restrictSelection = ref(program.value.restrict_to_notification_channels);
  const onRestrictChange = (val) => {
    restrictSelection.value = val;
    dispatch('shoutoutsProgram/updateRestrictToNotificationChannels', { val });
  };
  const channelsHelp = computed(() => {
    let text = `Add at least one channel where Donut can communicate about ${featureName}.`;
    if (!restrictSelection.value) {
      text += ` You will still be able to use ${featureName} in any channel Donut is in, unless you update your settings.`;
    }
    return text;
  });

  const channelsSelection = ref(program.value.channels);
  const isLoading = ref(false);
  // When restricted, show only the selected channels.
  const displayChannelPhrase = computed(() => {
    if (restrictSelection.value && channelsSelection.value.length) {
      return toSentence(channelsSelection.value.map((c) => c.name), 'and');
    }
    return props.channelPhrase;
  });
  const onChannelsUpdate = (newChannelList) => {
    // This is currently keeping track of the channels list in two places, channelsSelection above and in state at
    // shoutoutsProgram.program. This is not ideal, but the decision to have this selector async make an API POST
    // on selection has revealed a lack of flexibility in the way the selectors are set up. An issue for another time.
    isLoading.value = true;
    channelsSelection.value = newChannelList;
    const channelsParam = channelsSelection.value.map((c) => (c.id ? c.id : c.name.replace(/^#/, '')));
    dispatch('shoutoutsProgram/updateProgram', {
      params: { channels: channelsParam },
      successToast: 'Successfully updated notification channels!',
      errorToast: 'Failed to update notification channels.',
    }).then(() => { isLoading.value = false; });
  };

  // New Channels
  const addNewChannel = (channelName) => {
    onChannelsUpdate([...channelsSelection.value, { name: `#${channelName}` }]);
  };
  const openNewChannelModal = () => {
    window.donut.track('Shoutouts Program - Opened New Channel Modal');
    commit('updateModal', {
      visible: true,
      subcomponent: 'shoutouts-programs-modals-new-channel',
      data: { onSubmit: addNewChannel },
    });
  };
</script>
